import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "1. Definitions",
    content: [
      { term: '"Platform"', def: 'refers to the TumaTrack web application, mobile application, and all associated APIs and services.' },
      { term: '"Shop Owner"', def: 'refers to any business or individual who registers a shop on TumaTrack to manage deliveries.' },
      { term: '"Rider"', def: 'refers to any delivery personnel added by a Shop Owner to fulfill delivery orders.' },
      { term: '"Customer"', def: 'refers to the end recipient of a delivery order, who may access tracking information via a public tracking link.' },
      { term: '"Subscription"', def: 'refers to a paid plan (Starter, Growth, or Enterprise) that grants a Shop Owner access to the Platform\'s features.' },
      { term: '"Trial Period"', def: 'refers to the 7-day free access period granted to newly approved Shop Owners.' },
    ]
  },
  {
    title: "2. Eligibility and Registration",
    items: [
      "You must be at least 18 years of age to register on TumaTrack.",
      "Shop Owners must provide accurate business information including a valid KRA PIN and/or business registration number. Submission of false or misleading information constitutes grounds for immediate account termination.",
      "Each KRA PIN and registered phone number may only be associated with one TumaTrack account. Creating multiple accounts to circumvent subscription requirements is strictly prohibited.",
      "TumaTrack reserves the right to verify business information and reject or suspend any application at its sole discretion.",
      "You are responsible for maintaining the confidentiality of your account credentials.",
    ]
  },
  {
    title: "3. Account Approval Process",
    items: [
      "All Shop Owner registrations are subject to manual review and approval by TumaTrack administrators.",
      "Upon successful OTP verification, your account will be placed in a pending state until reviewed.",
      "TumaTrack will notify you via SMS and email of approval or rejection.",
      "Approved accounts receive a 7-day free Trial Period commencing from the date of approval.",
      "TumaTrack reserves the right to revoke approval at any time if submitted information was inaccurate or the account violates these Terms.",
    ]
  },
  {
    title: "4. Subscription Plans and Billing",
    items: [
      "Following the Trial Period, continued access requires a paid Subscription: Starter — KES 999/month (up to 3 Riders), Growth — KES 2,499/month (up to 10 Riders), Enterprise — KES 5,999/month (unlimited Riders).",
      "Subscription fees are billed monthly via M-Pesa. By subscribing, you authorize TumaTrack to initiate payment requests to your registered M-Pesa number on your monthly renewal date.",
      "Failure to complete payment within 24 hours of the renewal prompt will result in account suspension.",
      "Subscription fees are non-refundable except where required by applicable Kenyan law.",
      "TumaTrack reserves the right to modify pricing with 30 days' written notice to active subscribers.",
    ]
  },
  {
    title: "5. Shop Owner Responsibilities",
    items: [
      "Shop Owners are solely responsible for the accuracy of all Order information including customer names, phone numbers, delivery addresses, and fees.",
      "Shop Owners are responsible for ensuring Riders have consented to share location data while on active deliveries.",
      "Shop Owners must not use the Platform to manage deliveries of illegal, prohibited, or hazardous goods.",
      "Shop Owners are responsible for resolving disputes with Customers. TumaTrack is not a party to any commercial transaction between a Shop Owner and their Customer.",
      "Shop Owners are responsible for compliance with all applicable Kenyan business laws and tax obligations.",
    ]
  },
  {
    title: "6. Rider Responsibilities",
    items: [
      "Riders are added by Shop Owners and operate under their direction. TumaTrack does not employ Riders and is not responsible for Rider conduct.",
      "Riders consent to real-time location tracking while on active deliveries. This data is used solely to provide delivery tracking.",
      "Riders are responsible for the safe and timely handling of all delivery orders.",
      "Riders must not misuse Platform features including falsifying location data or order status updates.",
      "Rider location data is retained for 90 days then permanently deleted.",
    ]
  },
  {
    title: "7. Data Privacy and Protection",
    items: [
      "TumaTrack is committed to protecting personal data in accordance with the Kenya Data Protection Act, 2019. See our Privacy Policy for full details.",
      "Data collected includes: business registration details, contact information, order data, location data (Riders), and usage data.",
      "TumaTrack uses Africa's Talking for SMS and Resend for email. Message content is limited to service-related notifications only.",
      "TumaTrack implements industry-standard security measures including encrypted data transmission (TLS) and tokenized authentication.",
      "You may request deletion of your account and associated data. Requests will be processed within 30 days.",
    ]
  },
  {
    title: "8. Limitation of Liability",
    items: [
      'TumaTrack provides the Platform on an "as is" and "as available" basis without warranty of uninterrupted or error-free service.',
      "TumaTrack is not liable for delivery failures by Riders, incorrect information entered by Shop Owners, loss of goods during transit, or network outages affecting tracking.",
      "TumaTrack's total liability shall not exceed subscription fees paid by that user in the 3 months preceding any claim.",
    ]
  },
  {
    title: "9. Termination and Suspension",
    items: [
      "You may terminate your account at any time by contacting TumaTrack support. No refund will be issued for the remainder of a billing period.",
      "TumaTrack may suspend or terminate your account immediately for violations of these Terms, false registration information, non-payment, or illegal use.",
      "Upon termination, order history is retained for 90 days before permanent deletion. You may request an export during this period.",
    ]
  },
  {
    title: "10. Governing Law and Dispute Resolution",
    items: [
      "These Terms are governed by the laws of the Republic of Kenya.",
      "Disputes shall first be addressed through good-faith negotiation, then mediation under the Nairobi Centre for International Arbitration (NCIA) Mediation Rules.",
      "The parties submit to the jurisdiction of the courts of Nairobi County, Kenya.",
      "Relevant legislation includes: the Kenya Information and Communications Act, the Data Protection Act 2019, and the Consumer Protection Act 2012.",
    ]
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Navbar */}
      <nav className="bg-slate-900 px-5 h-14 flex items-center justify-between sticky top-0 z-40">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 text-white" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
          </div>
          <span className="text-white font-bold text-sm" style={{ fontFamily: "'Sora', sans-serif" }}>
            Tuma<span className="text-orange-400">Track</span>
          </span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
            Terms and Conditions
          </h1>
          <p className="text-gray-500 text-sm">Effective Date: July 2026 · Governing Jurisdiction: Nairobi County, Kenya</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-orange-700 text-xs font-medium px-4 py-2 rounded-full">
            TumaTrack Technologies · Business Reg. BN-L3SM6PEB · KRA PIN A013216473T
          </div>
        </div>

        {/* Intro */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <p className="text-gray-600 text-sm leading-relaxed">
            Please read these Terms and Conditions carefully before registering for or using the TumaTrack platform. By creating an account, subscribing to a plan, or using any feature of TumaTrack, you agree to be legally bound by these Terms. If you do not agree, you must not use the Platform. See also our{" "}
            <Link to="/privacy" className="text-orange-500 hover:text-orange-600 font-medium">
              Privacy Policy
            </Link>.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-gray-900 mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
                {section.title}
              </h2>
              {section.content ? (
                <div className="space-y-3">
                  {section.content.map((row) => (
                    <div key={row.term} className="flex gap-2 text-sm">
                      <span className="font-semibold text-gray-900 shrink-0">{row.term}</span>
                      <span className="text-gray-500">— {row.def}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-gray-600">
                      <span className="text-orange-400 font-bold shrink-0 mt-0.5">·</span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-6 bg-slate-900 rounded-2xl p-6 text-center">
          <p className="text-white font-bold mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>Questions about these Terms?</p>
          <p className="text-slate-400 text-sm">
            Email us at{" "}
            <a href="mailto:support@tumatrack.co.ke" className="text-orange-400 hover:text-orange-300">
              support@tumatrack.co.ke
            </a>
            {" "}or use the{" "}
            <Link to="/contact" className="text-orange-400 hover:text-orange-300">
              contact form
            </Link>
          </p>
          <Link to="/register" className="inline-flex mt-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">
            Back to Registration
          </Link>
        </div>
      </div>
    </div>
  );
}
